import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  FLOORPLAN_FOCUS_SCALE,
  FLOORPLAN_MAX_SCALE,
  FLOORPLAN_MIN_SCALE,
  FLOORPLAN_PAN_LIMIT_BASE,
  FLOORPLAN_PAN_LIMIT_PER_SCALE,
  FLOORPLAN_WHEEL_ZOOM_IN_STEP,
  FLOORPLAN_WHEEL_ZOOM_OUT_STEP,
  FLOORPLAN_ZOOM_IN_STEP,
  FLOORPLAN_ZOOM_OUT_STEP,
} from 'floorplan/pages/native-floorplan/const';
import { FloorplanPlacementGeometry } from 'floorplan/pages/native-floorplan/types';

interface Transform {
  scale: number;
  x: number;
  y: number;
}

interface UseFloorplanZoomValues {
  transform: string;
  /** The zoom on its own, for anything drawn inside the plan that must not scale with it. */
  scale: number;
  isPannable: boolean;
  canZoomIn: boolean;
  canZoomOut: boolean;
  zoomIn: VoidFunction;
  zoomOut: VoidFunction;
  /** Centres the given placement in the viewport at the jump zoom (a coworker "View", §C2). */
  focusOn: (geometry: FloorplanPlacementGeometry) => void;
}

const INITIAL: Transform = { scale: FLOORPLAN_MIN_SCALE, x: 0, y: 0 };

const clampScale = (value: number): number => Math.max(FLOORPLAN_MIN_SCALE, Math.min(FLOORPLAN_MAX_SCALE, value));

const panLimit = (scale: number): number => FLOORPLAN_PAN_LIMIT_PER_SCALE * (scale - 1) + FLOORPLAN_PAN_LIMIT_BASE;

const clampOffset = (value: number, scale: number): number => {
  const limit = panLimit(scale);

  return Math.max(-limit, Math.min(limit, value));
};

/**
 * Applies a new scale, keeping the offset valid for it. Shared by the buttons, the wheel and the
 * pinch so they cannot drift: back at 1x the whole plan fits, so any leftover offset would only
 * hide part of it, and zooming out shrinks how far the plan may be pushed.
 */
const withScale = (current: Transform, nextScale: number): Transform =>
  nextScale <= FLOORPLAN_MIN_SCALE
    ? INITIAL
    : { scale: nextScale, x: clampOffset(current.x, nextScale), y: clampOffset(current.y, nextScale) };

const touchDistance = (touches: TouchList): number =>
  Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);

/**
 * Zoom and pan for the floorplan, matching the prototype: 1x–3.2x, buttons stepping by 1.35 / 0.74,
 * wheel by 1.12 / 0.9, two-finger pinch, and dragging that only kicks in past 1x.
 *
 * Listeners are attached imperatively because wheel and touchmove must be non-passive to call
 * `preventDefault` — React's `onWheel`/`onTouchMove` register as passive and the browser would keep
 * scrolling the page underneath.
 *
 * Zooming back out to 1x recentres, so the plan can never be left parked off-screen.
 */
export const useFloorplanZoom = (
  viewportRef: RefObject<HTMLElement>,
  resetKey?: string,
  /** The plan's own box (image + overlay); the viewport is larger by its inset. */
  planRef?: RefObject<HTMLElement>,
): UseFloorplanZoomValues => {
  const [{ scale, x, y }, setTransform] = useState<Transform>(INITIAL);
  // Gesture bookkeeping lives in refs: it changes on every pointer move and must not re-render.
  const panRef = useRef<{ x: number; y: number; originX: number; originY: number } | null>(null);
  const pinchRef = useRef<{ distance: number; scale: number } | null>(null);
  const scaleRef = useRef(scale);
  // The current offset is read synchronously when a drag starts; going through a state updater
  // would run too late — the mousedown handler needs to know right away whether a pan began.
  const offsetRef = useRef({ x, y });

  scaleRef.current = scale;
  offsetRef.current = { x, y };

  const zoomBy = useCallback((factor: number) => {
    setTransform(current => withScale(current, clampScale(current.scale * factor)));
  }, []);

  // A new floor is a new drawing; keeping the previous zoom would land the user somewhere arbitrary.
  useEffect(() => setTransform(INITIAL), [resetKey]);

  /**
   * The transform origin is the centre of the plan, so putting a point at fraction (cx, cy) in the
   * middle of the viewport means shifting by its distance from the centre, scaled. The pan limit is
   * widened to the geometric bound — the scaled plan's edge reaching the viewport's — because the
   * gesture limit is tuned for dragging, and a centred desk is by definition on screen.
   */
  const focusOn = useCallback(
    (geometry: FloorplanPlacementGeometry) => {
      const plan = planRef?.current;

      if (!plan) {
        return;
      }

      const nextScale = clampScale(FLOORPLAN_FOCUS_SCALE);
      const centerX = geometry.x + geometry.width / 2;
      const centerY = geometry.y + geometry.height / 2;
      const limitX = Math.max(((nextScale - 1) * plan.clientWidth) / 2, panLimit(nextScale));
      const limitY = Math.max(((nextScale - 1) * plan.clientHeight) / 2, panLimit(nextScale));
      const offsetX = -(centerX - 0.5) * plan.clientWidth * nextScale;
      const offsetY = -(centerY - 0.5) * plan.clientHeight * nextScale;

      setTransform({
        scale: nextScale,
        x: Math.max(-limitX, Math.min(limitX, offsetX)),
        y: Math.max(-limitY, Math.min(limitY, offsetY)),
      });
    },
    [planRef],
  );

  const movePan = useCallback((clientX: number, clientY: number) => {
    const pan = panRef.current;

    if (!pan) {
      return;
    }

    setTransform(current => ({
      ...current,
      x: clampOffset(pan.originX + (clientX - pan.x), current.scale),
      y: clampOffset(pan.originY + (clientY - pan.y), current.scale),
    }));
  }, []);

  /** Returns whether a pan actually started — there is nothing to drag at 1x. */
  const startPan = useCallback((clientX: number, clientY: number): boolean => {
    if (scaleRef.current <= FLOORPLAN_MIN_SCALE) {
      return false;
    }

    panRef.current = { x: clientX, y: clientY, originX: offsetRef.current.x, originY: offsetRef.current.y };

    return true;
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;

    if (!viewport) {
      return undefined;
    }

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      zoomBy(event.deltaY < 0 ? FLOORPLAN_WHEEL_ZOOM_IN_STEP : FLOORPLAN_WHEEL_ZOOM_OUT_STEP);
    };

    const onMouseMove = (event: MouseEvent) => movePan(event.clientX, event.clientY);
    const endPan = () => {
      panRef.current = null;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', endPan);
    };

    const onMouseDown = (event: MouseEvent) => {
      if (startPan(event.clientX, event.clientY)) {
        // Tracked on the window so a drag that leaves the map still follows the cursor.
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', endPan);
      }
    };

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length === 2) {
        pinchRef.current = { distance: touchDistance(event.touches), scale: scaleRef.current };
      } else if (event.touches.length === 1) {
        startPan(event.touches[0].clientX, event.touches[0].clientY);
      }
    };

    const onTouchMove = (event: TouchEvent) => {
      const pinch = pinchRef.current;

      if (event.touches.length === 2 && pinch) {
        event.preventDefault();
        const nextScale = clampScale((pinch.scale * touchDistance(event.touches)) / pinch.distance);

        setTransform(current => withScale(current, nextScale));
      } else if (panRef.current) {
        event.preventDefault();
        movePan(event.touches[0].clientX, event.touches[0].clientY);
      }
    };

    const onTouchEnd = () => {
      panRef.current = null;
      pinchRef.current = null;
    };

    viewport.addEventListener('wheel', onWheel, { passive: false });
    viewport.addEventListener('mousedown', onMouseDown);
    viewport.addEventListener('touchstart', onTouchStart, { passive: false });
    viewport.addEventListener('touchmove', onTouchMove, { passive: false });
    viewport.addEventListener('touchend', onTouchEnd);

    return () => {
      viewport.removeEventListener('wheel', onWheel);
      viewport.removeEventListener('mousedown', onMouseDown);
      viewport.removeEventListener('touchstart', onTouchStart);
      viewport.removeEventListener('touchmove', onTouchMove);
      viewport.removeEventListener('touchend', onTouchEnd);
      endPan();
    };
  }, [viewportRef, zoomBy, movePan, startPan]);

  return useMemo(
    () => ({
      transform: `translate(${x}px, ${y}px) scale(${scale})`,
      scale,
      isPannable: scale > FLOORPLAN_MIN_SCALE,
      canZoomIn: scale < FLOORPLAN_MAX_SCALE,
      canZoomOut: scale > FLOORPLAN_MIN_SCALE,
      zoomIn: () => zoomBy(FLOORPLAN_ZOOM_IN_STEP),
      zoomOut: () => zoomBy(FLOORPLAN_ZOOM_OUT_STEP),
      focusOn,
    }),
    [x, y, scale, zoomBy, focusOn],
  );
};
